package com.macrofocus.eflux.ui.table

import com.macrofocus.common.format.CPFormat
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.common.selection.MutableSelection
import com.macrofocus.common.selection.MutableSingleSelection
import com.macrofocus.common.units.Quantity
import com.macrofocus.eflux.DataLoader
import com.macrofocus.eflux.network.DerivedBranchColumn
import com.macrofocus.eflux.network.line.Line
import com.macrofocus.eflux.network.logical.LogicalBranchId
import com.macrofocus.eflux.network.logical.LogicalBusId
import com.macrofocus.eflux.network.logical.logicalBranchIdOf
import com.macrofocus.eflux.network.logical.logicalBusIdOf
import com.macrofocus.eflux.network.toFixed
import com.macrofocus.eflux.ui.data.LineButton
import com.macrofocus.eflux.ui.data.SubstationChip
import com.macrofocus.eflux.ui.data.risk.*
import com.macrofocus.high_d.table.DefaultTable
import com.macrofocus.high_d.table.ShowFilteredDataFrame
import js.objects.unsafeJso
import mui.icons.material.BatteryUnknownTwoTone
import mui.material.*
import mui.system.sx
import mui.x.GridRenderCellParams
import org.mkui.react.useSelection
import org.molap.dataframe.DataFrame
import org.molap.dataframe.toDataFrame
import react.FC
import react.Props
import react.ReactElement
import react.create
import web.cssom.*

external interface BranchTableProps : Props {
    var dataLoader: DataLoader
    var rows: Iterable<Line>?
    var columns: Array<DerivedBranchColumn>
    var probing: MutableSingleSelection<LogicalBranchId>
    var selection: MutableSelection<LogicalBranchId>
    var logicalBusProbing: MutableSingleSelection<LogicalBusId>
    var logicalBusSelection: MutableSelection<LogicalBusId>
    var transparent: BackgroundColor?
    var filter: ReactElement<*>
}

val BranchTable = FC<BranchTableProps> { props ->
    val rows = props.rows
    val selection = useSelection(props.selection)

    if(rows != null) {
        val dataFrame: DataFrame<Line, String, Any?> = rows.toList().toDataFrame {
            for (column in props.columns) {
                column.name to { column.getValue(this.branches!!.branch) }
            }
//            "temperature" to { this.riskMap?.let { it.and(512) != 0 } }
//            "wind_gusts" to { this.riskMap?.let { it.and(256) != 0 } }
//            "mudslide" to { this.riskMap?.let { it.and(32) != 0 } }
//            "flooding" to { this.riskMap?.let { it.and(16) != 0 } }
//            "lightning_expected" to { this.riskMap?.let { it.and(4) != 0 } }
//            "lightning_activity" to { this.riskMap?.let { it.and(2) != 0 } }
//            "lightning_hit" to { this.riskMap?.let { it.and(1) != 0 } }
            "temperature" to { this.temperature_count }
            "wind_gusts" to { this.wind_gusts_count }
            "mudslide" to { this.mudslide_count }
            "flooding" to { this.flooding_count }
            "lightning_expected" to { this.lightning_expected_count }
            "lightning_activity" to { this.lightning_activity_count }
            "lightning_hit" to { this.lightning_hit_count }
        }

//        val factory = HighDFactory<Line, String, Any?>(HighDSettings(), ColorMapFactory.instance)
//        val table = factory.createTable()
        val table = DefaultTable<Line, String, Any?>()
        table.filter = props.filter
        for (column in props.columns) {
            table.setLabel(column.name, column.longName)
        }
        table.setLabel(DerivedBranchColumn.logicalId.name, "Line")

        table.setWidths(DerivedBranchColumn.zone.name, 60, 40, 60)
        table.setWidths(DerivedBranchColumn.logicalId.name, 250, 100, 1000)
        table.setWidths(DerivedBranchColumn.from.name, 130, 100, 300)
        table.setWidths(DerivedBranchColumn.to.name, 130, 100, 300)
        table.setWidths(DerivedBranchColumn.st.name, 60, 20, 60)
        table.setWidths(DerivedBranchColumn.baskv.name, 100, 40, 100)
        table.setWidths(DerivedBranchColumn.p.name, 100, 60, 100)
        table.setWidths(DerivedBranchColumn.ratea.name, 100, 60, 100)
        table.setWidths(DerivedBranchColumn.pratea.name, 100, 30, 100)
        table.setWidths(DerivedBranchColumn.r.name, 100, 30, 100)
        table.setWidths(DerivedBranchColumn.x.name, 100, 30, 100)

        val risks = arrayOf("lightning_hit", "lightning_activity", "lightning_expected", "flooding", "mudslide", "wind_gusts", "temperature")
        for (risk in risks) {
            table.setWidths(risk, 60, 40, 60)
            table.setFormat(risk, object: CPFormat<Any?> {
                override fun format(value: Any?): String? {
                    if(value is Int) {
                        return value.toString()
                    } else {
                        return ""
                    }
                }

                override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
                    TODO("Not yet implemented")
                }

                override fun parse(text: String?): Any? {
                    TODO("Not yet implemented")
                }

                override val pattern: String?
                    get() = TODO("Not yet implemented")
                override val nativeFormat: Any?
                    get() = TODO("Not yet implemented")
                override val horizontalAlignment: CPFormat.HorizontalAlignment?
                    get() = CPFormat.HorizontalAlignment.Right

                override fun getClassName(value: Any?): String? {
                    TODO("Not yet implemented")
                }
            })
        }

        table.setHeaderRenderer("lightning_hit", { LightningHitIcon.create() })
        table.setHeaderRenderer("lightning_activity", { LightningActivityIcon.create() })
        table.setHeaderRenderer("lightning_expected", { LightningExpectedIcon.create() })
        table.setHeaderRenderer("flooding", { FloodingIcon.create() })
        table.setHeaderRenderer("mudslide", { MudslideIcon.create() })
        table.setHeaderRenderer("wind_gusts", { WindGustsIcon.create() })
        table.setHeaderRenderer("temperature", { TemperatureIcon.create() })

        table.setSortingOrder("lightning_hit", arrayOf("desc", null))
        table.setSortingOrder("lightning_activity", arrayOf("desc", null))
        table.setSortingOrder("lightning_expected", arrayOf("desc", null))
        table.setSortingOrder("flooding", arrayOf("desc", null))
        table.setSortingOrder("mudslide", arrayOf("desc", null))
        table.setSortingOrder("wind_gusts", arrayOf("desc", null))
        table.setSortingOrder("temperature", arrayOf("desc", null))

        table.setGridCellRenderer("logicalBranchId", { params: GridRenderCellParams ->
            if(params.value is LogicalBranchId) {
                val element = LineButton.create() {
                    this.dataLoader = props.dataLoader
                    this.primary = true
                    this.probing = props.probing
                    this.selection = props.selection
                    this.logicalBranchId = params.value
                    this.filled = true
                    this.risks = false
                }
                element
            } else if(params.value is String) {
                val element = LineButton.create() {
                    this.dataLoader = props.dataLoader
                    this.primary = true
                    this.probing = props.probing
                    this.selection = props.selection
                    this.logicalBranchId = logicalBranchIdOf(params.value)
                    this.filled = true
                    this.risks = false
                }
                element
            } else {
                Button.create { BatteryUnknownTwoTone {} }
            }
        })
        table.setGridCellRenderer("from", { params: GridRenderCellParams ->
            if(params.value is LogicalBusId) {
                val element = SubstationChip.create() {
                    this.dataLoader = props.dataLoader
                    this.primary = true
                    this.probing = props.logicalBusProbing
                    this.selection = props.logicalBusSelection
                    this.logicalBusId = params.value
                    this.placement = TooltipPlacement.left
                }
                element
            } else if(params.value is String) {
                val element = SubstationChip.create() {
                    this.dataLoader = props.dataLoader
                    this.primary = true
                    this.probing = props.logicalBusProbing
                    this.selection = props.logicalBusSelection
                    this.logicalBusId = logicalBusIdOf(params.value)
                    this.placement = TooltipPlacement.left
                }
                element
            } else {
                Button.create { BatteryUnknownTwoTone {} }
            }
        })
        table.setGridCellRenderer("to", { params: GridRenderCellParams ->
            if(params.value is LogicalBusId) {
                val element = SubstationChip.create() {
                    this.dataLoader = props.dataLoader
                    this.primary = true
                    this.probing = props.logicalBusProbing
                    this.selection = props.logicalBusSelection
                    this.logicalBusId = params.value
                    this.placement = TooltipPlacement.right
                }
                element
            } else if(params.value is String) {
                val element = SubstationChip.create() {
                    this.dataLoader = props.dataLoader
                    this.primary = true
                    this.probing = props.logicalBusProbing
                    this.selection = props.logicalBusSelection
                    this.logicalBusId = logicalBusIdOf(params.value)
                    this.placement = TooltipPlacement.right
                }
                element
            } else {
                Button.create { BatteryUnknownTwoTone {} }
            }
        })
        table.setFormat("baskv", object: CPFormat<Any?> {
            override fun format(value: Any?): String? {
                if(value is Quantity<*>) {
                    return value.amount.toFixed(0) + " " + value.unit.symbol
                } else {
                    return ""
                }
            }

            override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
                TODO("Not yet implemented")
            }

            override fun parse(text: String?): Any? {
                TODO("Not yet implemented")
            }

            override val pattern: String?
                get() = TODO("Not yet implemented")
            override val nativeFormat: Any?
                get() = TODO("Not yet implemented")
            override val horizontalAlignment: CPFormat.HorizontalAlignment?
                get() = CPFormat.HorizontalAlignment.Right

            override fun getClassName(value: Any?): String? {
                TODO("Not yet implemented")
            }
        })
        table.setFormat("p", object: CPFormat<Any?> {
            override fun format(value: Any?): String? {
                if(value is Quantity<*>) {
                    return value.amount.toFixed(0) + " " + value.unit.symbol
                } else {
                    return ""
                }
            }

            override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
                TODO("Not yet implemented")
            }

            override fun parse(text: String?): Any? {
                TODO("Not yet implemented")
            }

            override val pattern: String?
                get() = TODO("Not yet implemented")
            override val nativeFormat: Any?
                get() = TODO("Not yet implemented")
            override val horizontalAlignment: CPFormat.HorizontalAlignment?
                get() = CPFormat.HorizontalAlignment.Right

            override fun getClassName(value: Any?): String? {
                TODO("Not yet implemented")
            }
        })
        table.setFormat("ratea", object: CPFormat<Any?> {
            override fun format(value: Any?): String? {
                if(value is Quantity<*>) {
                    return value.amount.toFixed(0) + " " + value.unit.symbol
                } else {
                    return ""
                }
            }

            override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
                TODO("Not yet implemented")
            }

            override fun parse(text: String?): Any? {
                TODO("Not yet implemented")
            }

            override val pattern: String?
                get() = TODO("Not yet implemented")
            override val nativeFormat: Any?
                get() = TODO("Not yet implemented")
            override val horizontalAlignment: CPFormat.HorizontalAlignment?
                get() = CPFormat.HorizontalAlignment.Right

            override fun getClassName(value: Any?): String? {
                TODO("Not yet implemented")
            }
        })
        table.setFormat("pratea", object: CPFormat<Any?> {
            override fun format(value: Any?): String? {
                if(value is Double) {
                    return value.times(100.0).toFixed(1) + "%"
                } else {
                    return ""
                }
            }

            override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
                TODO("Not yet implemented")
            }

            override fun parse(text: String?): Any? {
                TODO("Not yet implemented")
            }

            override val pattern: String?
                get() = TODO("Not yet implemented")
            override val nativeFormat: Any?
                get() = TODO("Not yet implemented")
            override val horizontalAlignment: CPFormat.HorizontalAlignment?
                get() = CPFormat.HorizontalAlignment.Right

            override fun getClassName(value: Any?): String? {
                TODO("Not yet implemented")
            }
        })
        table.setFormat("r", object: CPFormat<Any?> {
            override fun format(value: Any?): String? {
                if(value is Quantity<*>) {
                    return value.amount.toFixed(3) + " " + value.unit.symbol
                } else {
                    return ""
                }
            }

            override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
                TODO("Not yet implemented")
            }

            override fun parse(text: String?): Any? {
                TODO("Not yet implemented")
            }

            override val pattern: String?
                get() = TODO("Not yet implemented")
            override val nativeFormat: Any?
                get() = TODO("Not yet implemented")
            override val horizontalAlignment: CPFormat.HorizontalAlignment?
                get() = CPFormat.HorizontalAlignment.Right

            override fun getClassName(value: Any?): String? {
                TODO("Not yet implemented")
            }
        })
        table.setFormat("x", object: CPFormat<Any?> {
            override fun format(value: Any?): String? {
                if(value is Quantity<*>) {
                    return value.amount.toFixed(3) + " " + value.unit.symbol
                } else {
                    return ""
                }
            }

            override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
                TODO("Not yet implemented")
            }

            override fun parse(text: String?): Any? {
                TODO("Not yet implemented")
            }

            override val pattern: String?
                get() = TODO("Not yet implemented")
            override val nativeFormat: Any?
                get() = TODO("Not yet implemented")
            override val horizontalAlignment: CPFormat.HorizontalAlignment?
                get() = CPFormat.HorizontalAlignment.Right

            override fun getClassName(value: Any?): String? {
                TODO("Not yet implemented")
            }
        })
        table.dataFrame = ShowFilteredDataFrame(dataFrame, dataFrame, SimpleProperty(true))

        table.nativeComponent()
        return@FC
    }

    TableContainer {
        sx = unsafeJso {
            position = Position.absolute
            top = 0.px
            bottom = 0.px
            if(props.transparent != null) {
                backgroundColor = props.transparent
            }
        }

        component = Paper.create().type

        Table {
            sx = unsafeJso {
                minWidth = 650.px
                // Enable horizontal scrolling
//                    overflowX = Auto.auto
            }

            size = Size.small

            ariaLabel = "simple table"
            stickyHeader = true

            TableHead {
                TableRow {
                    for (column in props.columns) {
                        TableCell {
                            +"${column.longName}"
                        }
                    }
                }
//                TableRow {
//                    for (column in props.columns) {
//                        TableCell {
//                            +"${column.name}"
//                        }
//                    }
//                }
            }
            if (rows != null) {
                TableBody {
                    for (row in rows) {
                        TableRow {
                            onMouseOver = {
                                props.probing.selected = row.logicalBranchId
                            }
                            onMouseDown = {
                                props.selection.setSelectedElement(row.logicalBranchId)
                            }

                            //                        key = row.toString()
                            //
                            selected = props.selection.isSelected(row.logicalBranchId)

                            for (column in props.columns) {
                                TableCell {
                                    sx {
                                        whiteSpace = WhiteSpace.nowrap
                                    }
                                    //                                //                                                        component = ReactHTML.th
                                    //                                //                                                        scope = "row"
                                    //
                                    val value = column.getValue(row.branches!!.branch)
                                    if(value is LogicalBranchId) {
                                        LineButton {
                                            this.dataLoader = props.dataLoader
                                            this.primary = true
                                            this.probing = props.probing
                                            this.selection = props.selection
                                            this.logicalBranchId = value
                                            this.filled = true
                                            this.risks = false
                                        }
                                    } else {
                                        if (value is Quantity<*> || value is Number) {
                                            align = TableCellAlign.right
                                        }
                                        val formattedValue = column.getFormattedValue(row.branches.branch)
                                        if (formattedValue != null) {
                                            +formattedValue
                                        } else {
                                            if (value != null) {
                                                +(value.toString())
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
